import _ from 'lodash';
import { columnIdToColumnName } from '../utils';
import { OrderEntryTableRow } from './columns';
import { formatQuantity } from 'src/utils/quantity-utils';
import { ResolvedQuantity } from 'src/domain/property-types';
import { ColumnType } from 'src/gql/graphql';
import { formatUnit, parseUnit } from 'src/utils/unit-utils';

// --- Types ---
type Predicate =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUAL'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUAL'
  | 'IN'
  | 'NOT_IN'
  | 'ANY_OF'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'EXISTS'
  | 'NOT_EXISTS'
  | 'FROM_REGEX';

interface Filter {
  id: string; // column ID
  value: Array<{
    predicate: Predicate;
    predicateValues: string[];
  }>;
}

type AggregationConfig = Record<string, string>;
type ColumnNameMap = {
  id: string;
  name: string;
  type: ColumnType;
};
// type AggregationOp = 'sum' | 'avg' | 'count' | 'min' | 'max';
// type AggregationConfig = Record<string, AggregationOp>;

// --- Filtering Logic ---
function passesFilter(
  row: OrderEntryTableRow,
  filter: Filter,
  customColMap: ColumnNameMap[]
): boolean {
  let val = _.get(row, filter.id);
  if (!val && filter.id.startsWith('itemGroup') && row.itemGroups?.length) {
    val = row.itemGroups.find((gp) => Boolean(gp[filter.id]))?.[filter.id];
  }

  if (!val) {
    // We store the name mapping and remap before we kick things off so even renaming should work
    const customColName = customColMap.find(
      (col) => col.id === filter.id
    )?.name;
    const customField = row.customFields.find(
      ({ orderColumnName }) => orderColumnName === customColName
    );
    // Should we worry about units?
    val =
      customField?.resolvedValue?.value ||
      row.parentOrder.labels.find((lbl) => lbl.key === customColName)?.value;
  }
  // You need to map the filter id to the accessor key, we can pass in a function which has the accessor keys, and work some IoC magic
  // or I can iterate over the filters and map all the IDs to their accesor key equivalents

  // eslint-disable-next-line complexity
  return filter.value.every(({ predicate, predicateValues }) => {
    // For ANY_OF, always keep ref as array to ensure includes() works correctly
    const ref =
      predicate === 'ANY_OF' || predicate === 'IN' || predicate === 'NOT_IN'
        ? predicateValues
        : predicateValues.length === 1
        ? predicateValues[0]
        : predicateValues;

    if (typeof val?.value === 'number') {
      val = val.value;
    }

    switch (predicate) {
      case 'EQUALS':
        return val == ref;
      case 'NOT_EQUALS':
        return val != ref;
      case 'LESS_THAN':
        return Number(val) < Number(ref);
      case 'LESS_THAN_OR_EQUAL':
        return Number(val) <= Number(ref);
      case 'GREATER_THAN':
        return Number(val) > Number(ref);
      case 'GREATER_THAN_OR_EQUAL':
        return Number(val) >= Number(ref);
      case 'IN':
      case 'ANY_OF':
        return Array.isArray(ref) && ref.includes(String(val));
      case 'NOT_IN':
        return Array.isArray(ref) && !ref.includes(String(val));
      case 'CONTAINS':
        return typeof val === 'string' && val.includes(String(ref));
      case 'NOT_CONTAINS':
        return typeof val === 'string' && !val.includes(String(ref));
      case 'EXISTS':
        // Handle arrays that contain only null/undefined/empty values
        if (Array.isArray(val)) {
          return val.some((v) => v != null && v !== '' && v !== undefined);
        }
        return val != null && val !== '' && val !== undefined;
      case 'NOT_EXISTS':
        // Handle arrays that contain only null/undefined/empty values
        if (Array.isArray(val)) {
          return val.every((v) => v == null || v === '' || v === undefined);
        }
        return val == null || val === '' || val === undefined;
      case 'FROM_REGEX':
        try {
          const regex = new RegExp(String(ref));
          return regex.test(String(val));
        } catch {
          return false;
        }
      default:
        return true;
    }
  });
}

function applyFilters(
  data: OrderEntryTableRow[],
  filters: Filter[],
  customColMap: ColumnNameMap[]
): OrderEntryTableRow[] {
  if (!filters?.length) return data;
  return data.filter((row) =>
    filters.every((f) => passesFilter(row, f, customColMap))
  );
}

// --- Aggregation Logic ---
// eslint-disable-next-line complexity
function applyAggregations(
  rows: OrderEntryTableRow[],
  customColNameMap: ColumnNameMap[],
  aggregations: AggregationConfig
): Record<string, number | string> {
  const result: Record<string, number | string> = {};

  for (const key of Object.keys(aggregations)) {
    // let's just do it for the quantity column first then we'll generalize to the other kinds of columns
    if (key === 'resolvedQuantity') {
      // Filtration of null/undefined is done so that they are treated as zeros given they do not have units
      const values = rows.map((r) => r[key]).filter(Boolean);
      const valuesWithId: [string, ResolvedQuantity | null][] = rows.map(
        (r) => [r.id, r[key]]
      );
      const unitSet = new Set(
        valuesWithId
          .map((valWithId) => {
            const [key, val] = valWithId;
            if (!val) return null;
            const cUnits = val.units;
            if (cUnits) return parseUnit(cUnits);
            return rows.find((cRow) => cRow.id === key)?.unit;
          })
          .filter(Boolean)
      );
      // this should also catch if there's a mixture of correct units and bottom values
      if (unitSet.size > 1) {
        result[key] = '-';
        continue;
      }
      let sum = 0;
      for (const val of values) {
        if (!val) continue;
        if (Array.isArray(val.value) || typeof val.value === 'string') {
          // We do not have a solution for this yet
          result[key] = '-';
          break;
        }
        if (typeof val.value === 'number') {
          sum += val.value;
          continue;
        }
      }
      if (!result[key]) {
        result[key] = _.round(sum, 2);
      }
    }
    // Check if the customKey is in the customColumnMap passed in: it contains the ids of all the custom columns
    const customColIds = customColNameMap.map((col) => col.id);
    if (customColIds.includes(key)) {
      // Aggregation logic for custom keys
      const values = rows
        .flatMap((row) => {
          const colMap = customColNameMap.find((col) => col.id === key);
          const resolvedValue = row.customFields.find(
            (cf) => cf.orderColumnName === colMap?.name
          )?.resolvedValue;
          return resolvedValue;
        })
        .filter((val): val is ResolvedQuantity => Boolean(val));

      const unitSet = new Set(
        values
          // the && !!v.units, is meant to filter out nullish units
          // this allows us to be able to aggregate quantities with a mixture of a single unit and some unitless quantities
          .filter(
            (v): v is ResolvedQuantity => typeof v !== 'string' && !!v.units
          )
          .map((v) => v.units)
      );
      if (unitSet.size > 1) {
        result[key] = '-';
        continue;
      }

      let sum = 0;
      const textValues = new Set<string>();
      for (const val of values) {
        if (Array.isArray(val.value)) {
          // We do not have a solution for this yet
          result[key] = '-';
          break;
        }
        if (typeof val.value === 'number') {
          sum += val.value;
          continue;
        }
        if (typeof val.value === 'string') {
          textValues.add(val.value);
        }
      }
      if (result[key]) continue;
      if (textValues.size === 1 && sum === 0) {
        const [strVal] = textValues;
        result[key] = strVal;
        continue;
      }
      if (textValues.size === 0) {
        const [cUnit] = unitSet;
        const num = _.round(sum, 2);
        result[key] = unitSet.size <= 0 ? num : `${num} ${formatUnit(cUnit)}`;
        continue;
      }
      result[key] = '-';
    }
  }

  return result;
}

// --- Common Value Propagation ---
function extractCommonValues<T extends Record<string, any>>(
  rows: T[],
  aggregations: AggregationConfig
): Record<string, any> {
  if (!rows.length) return {};

  const keys = Object.keys(rows[0]);
  const commonValues: Record<string, any> = {};

  for (const key of keys) {
    if (key === 'subRows' || aggregations[key]) continue; // skip internal or aggregated columns

    const firstVal = rows[0][key];
    const allSame = rows.every((r) => _.isEqual(r[key], firstVal));
    if (allSame) {
      commonValues[key] = firstVal;
    }
  }

  return commonValues;
}

// --- Grouping Logic ---
// eslint-disable-next-line complexity
function iterateeFn(
  currentKey: string | number,
  obj: OrderEntryTableRow,
  customColNameMap: ColumnNameMap[]
) {
  if (
    currentKey === 'resolvedQuantity' &&
    typeof obj[currentKey]?.value === 'number'
  ) {
    return _.round(obj[currentKey]!.value as number, 2);
  }
  // check if we are grouping with a custom column
  const customCol = customColNameMap.find(
    (col) => col.id === currentKey && col.type === ColumnType.Custom
  );
  if (customCol) {
    const byName = obj.customFields.find(
      (field: any) => field.orderColumnName === customCol.name
    )?.resolvedValue;
    if (byName) return formatQuantity(byName);
  }
  const itemGp = customColNameMap.find(
    (col) => col.name === currentKey && col.type === ColumnType.ItemGroup
  );
  if (itemGp) {
    // Find this bad boy from the itemgroups
    const itemKey = itemGp.name;
    const correspondingGroup = obj.itemGroups?.find(
      (itemGroup) => itemGroup[itemKey]
    );
    if (!correspondingGroup || !correspondingGroup[itemKey])
      return `Missing ${itemKey}`;
    return correspondingGroup[itemKey];
  }

  const orderLblKey = customColNameMap.find(
    (col) => col.id === currentKey && col.type === ColumnType.ParentOrderLabel
  )?.name;
  if (orderLblKey) {
    // Check what the label value is
    const value = obj.parentOrder.labels.find(
      (lbl) => lbl.key === orderLblKey
    )?.value;
    if (!value) return `Missing ${orderLblKey}`;
    return value;
  }

  // Patch the parentorder.name && parentorder.status grouping
  if (String(currentKey).startsWith('parentOrder.')) {
    const subKey = String(currentKey).split('.').at(-1)!;
    return (obj.parentOrder as any)[subKey];
  }

  if (obj[currentKey as keyof OrderEntryTableRow])
    return obj[currentKey as keyof OrderEntryTableRow];
  // Maybe something more sophisticated
  let groupingColName = columnIdToColumnName(String(currentKey));
  if (groupingColName === currentKey) {
    const colMapping = customColNameMap.find((col) => col.id === currentKey);
    groupingColName = colMapping?.name || '';
  }

  return `Missing ${groupingColName}`;
}

function groupRecursive(
  data: OrderEntryTableRow[],
  groupKeys: string[],
  aggregations: AggregationConfig,
  customColNameMap: ColumnNameMap[],
  parentId?: string
): any[] {
  if (groupKeys.length === 0) return data;

  const [currentKey, ...restKeys] = groupKeys;
  const grouped = _.groupBy(data, (obj) =>
    iterateeFn(currentKey, obj, customColNameMap)
  );

  return Object.entries(grouped).map(([groupValue, rows]) => {
    const groupId = `${String(currentKey)}:${String(groupValue)}`;

    const node: Record<string, any> = {
      ...extractCommonValues(rows, aggregations),
      ...applyAggregations(rows, customColNameMap, aggregations),
      id: groupId,
      __isGrouped: true,
      [currentKey]: groupValue.startsWith('Missing') ? undefined : groupValue,
      // Ensure to ovveride the name if we have a grouping value
      name: groupValue,
    };

    node.subRows = restKeys.length
      ? groupRecursive(rows, restKeys, aggregations, customColNameMap, groupId)
      : rows;

    return node;
  });
}

// --- The Combined Function ---
export function processTableData(
  data: OrderEntryTableRow[],
  customColNameMap: ColumnNameMap[],
  filters: any[] = [],
  grouping: string[] = [],
  aggregations: AggregationConfig = {}
): any[] {
  // Step 1: Apply filters
  const filtered = applyFilters(data, filters, customColNameMap);

  // Step 2: Apply grouping + aggregation recursively
  const grouped = grouping.length
    ? groupRecursive(filtered, grouping, aggregations, customColNameMap)
    : filtered;

  return grouped;
}
